// Pure helpers for the dungeon-run state machine. Step 10a-1 lays down
// the data layer used by Step 10a-2's UI: starting a run, resolving its
// generated map, and moving the cursor along edges.
//
// Design notes:
//   - The DungeonMap itself is NOT persisted. Only `mapId` (the
//     `${templateId}#${seed}` string) lives in `DungeonRunState`. Callers
//     rebuild the map deterministically via `generateDungeonMap` whenever
//     they need it. This keeps save-slot rows small and lets the generator
//     evolve without a forced migration of stored maps.
//   - `NodeCoord` is `readonly [floor, index]` per @renavora/shared, but
//     React state and Sets compare by reference rather than value, so we
//     also offer a `"floor,index"` string form via `formatNodeKey` for
//     map-keyed lookups.

import {
  generateDungeonMap,
  type BorrowedAlly,
  type DungeonMap,
  type DungeonRunState,
  type DungeonTemplate,
  type DungeonTemplateId,
  type NodeCoord,
} from '@renavora/shared';

/** Inclusive upper bound for seeds — matches `Rng.fromSeed`'s 32-bit unsigned domain. */
export const MAX_SEED = 0xffffffff;

export interface StartDungeonRunOpts {
  /**
   * Borrowed allies selected in town. They are copied into the run with the
   * run's mapId so later party-slot edits cannot change an in-progress run.
   */
  readonly borrowedAllies?: readonly BorrowedAlly[];
  /** Unique run-instance id for once-per-run side effects. */
  readonly runInstanceId?: string;
}

/**
 * Format a `(templateId, seed)` pair as the `mapId` string stored in
 * `DungeonRunState`. Uses `#` as the delimiter because `#` is forbidden
 * inside `DungeonTemplateId` (see `DUNGEON_TEMPLATE_ID_PATTERN`).
 */
export function formatMapId(templateId: DungeonTemplateId, seed: number): string {
  return `${templateId}#${seed}`;
}

/**
 * Parse a `mapId` string back into its parts. Throws on malformed input —
 * callers treat that the same as "corrupt save slot" and abandon the run.
 * Rejects negative or out-of-range seeds and any string with extra `#`s.
 */
export function parseMapId(mapId: string): { templateId: DungeonTemplateId; seed: number } {
  const parts = mapId.split('#');
  if (parts.length !== 2) {
    throw new Error(`invalid mapId: expected exactly one '#' separator, got '${mapId}'`);
  }
  const [templateId, seedStr] = parts as [string, string];
  if (!/^dungeon\.[a-z][a-z0-9-]*\.t\d+$/.test(templateId)) {
    throw new Error(`invalid mapId: bad templateId in '${mapId}'`);
  }
  if (seedStr === '' || !/^\d+$/.test(seedStr)) {
    throw new Error(`invalid mapId: seed must be a non-negative integer in '${mapId}'`);
  }
  const seed = Number(seedStr);
  if (!Number.isInteger(seed) || seed < 0 || seed > MAX_SEED) {
    throw new Error(`invalid mapId: seed out of range in '${mapId}'`);
  }
  return { templateId: templateId as DungeonTemplateId, seed };
}

/**
 * Build the initial `DungeonRunState` for a freshly-started dungeon. All
 * resource fields start at their "untouched" defaults; sim integration in
 * Step 10b will mutate `partyHpRatio` / `activeBoons` / `penaltyPct`.
 */
export function startDungeonRun(
  templateId: DungeonTemplateId,
  seed: number,
  opts: StartDungeonRunOpts = {},
): DungeonRunState {
  const mapId = formatMapId(templateId, seed);
  return {
    mapId,
    runInstanceId: opts.runInstanceId ?? mapId,
    currentFloor: 1,
    currentNodeIndex: 0,
    partyHpRatio: 1,
    inventory: [],
    modifiersConsumed: [],
    activeBoons: [],
    penaltyPct: 0,
    cleared: false,
    borrowedAllies: (opts.borrowedAllies ?? []).map((ally) => ({
      ...ally,
      lockedForRunId: mapId,
    })),
  };
}

/** Caller-provided lookup so this module stays free of catalogue imports. */
export type TemplateLookup = (id: DungeonTemplateId) => DungeonTemplate | undefined;

/**
 * Resolve a `DungeonMap` from a `(templateId, seed)` pair. Throws if the
 * template id is unknown so callers don't render a half-built map.
 */
export function resolveMap(
  templateId: DungeonTemplateId,
  seed: number,
  lookup: TemplateLookup,
): DungeonMap {
  const template = lookup(templateId);
  if (template === undefined) {
    throw new Error(`unknown dungeon template: ${templateId}`);
  }
  return generateDungeonMap(template, seed);
}

/** Stringify a `NodeCoord` for use as a `Set<string>` / `Map<string>` key. */
export function formatNodeKey(coord: NodeCoord): string {
  return `${coord[0]},${coord[1]}`;
}

/**
 * True if `target` is reachable from `current` in one step along the
 * generated map's directed edges. Used by both the UI (to gate clicks)
 * and `applyAdvance` (to gate state mutation). Edge count is ≤ ~70 so a
 * linear scan is fine.
 */
export function canReachNode(map: DungeonMap, current: NodeCoord, target: NodeCoord): boolean {
  for (const edge of map.edges) {
    if (
      edge.from[0] === current[0] &&
      edge.from[1] === current[1] &&
      edge.to[0] === target[0] &&
      edge.to[1] === target[1]
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Advance the run to `target` if reachable. Returns a new state object;
 * throws if the move is illegal so the store can surface an error without
 * persisting a half-step. Floor-12 entry is allowed but doesn't set the
 * `cleared` flag — that's the boss-battle outcome's job (Step 10b).
 */
export function applyAdvance(
  state: DungeonRunState,
  map: DungeonMap,
  target: NodeCoord,
): DungeonRunState {
  const current: NodeCoord = [state.currentFloor, state.currentNodeIndex];
  if (!canReachNode(map, current, target)) {
    throw new Error(
      `node not reachable: from (${current[0]},${current[1]}) to (${target[0]},${target[1]})`,
    );
  }
  return {
    ...state,
    currentFloor: target[0],
    currentNodeIndex: target[1],
  };
}
